import i18n from '../i18n';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import moment from 'moment-timezone';
import React, {useState} from 'react';
import {Platform} from 'react-native';
import DeviceInfo from 'react-native-device-info';
import Toast from 'react-native-toast-message';
import {v4 as uuidv4} from 'uuid';
import type {RootState} from '../redux/store';

declare const localStorage: {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

export const apiLink = 'https://api.bluehms.com/api/';

const GlobalContext = React.createContext({});
export const GlobalProvider = GlobalContext.Provider;
export default GlobalContext;

// Logo bytes are served through the API (authenticated S3 read), not a raw S3/R2 URL.
export const buildLogoUrl = (
  companyId?: string | number | null,
  logoExt?: string | null,
): string =>
  companyId && logoExt ? `${apiLink}company/logo/${companyId}${logoExt}` : '';

// Resolves uploaded image keys to API proxy URLs
export const resolveImageUrl = (value?: string | null): string => {
  if (!value) return '';
  const legacy = value.match(/^https?:\/\/s3\.bluehms\.com\/[^/]+\/(.+)$/);
  if (legacy) return `${apiLink}company/image/${legacy[1]}`;
  if (/^https?:\/\//.test(value)) return value;
  return `${apiLink}company/image/${value}`;
};

// Web-safe function to get unique device ID
const getUniqueDeviceId = (): string => {
  try {
    if (Platform.OS === 'web') {
      // For web, use localStorage to store and retrieve a unique ID
      const storageKey = 'bluehms_device_id';
      let deviceId = localStorage.getItem(storageKey);
      if (!deviceId) {
        // Generate a unique ID for web using UUID
        deviceId = 'web_' + uuidv4();
        localStorage.setItem(storageKey, deviceId);
      }
      return deviceId;
    } else {
      // For native platforms, use DeviceInfo
      return DeviceInfo.getUniqueIdSync();
    }
  } catch (error) {
    console.warn('Error getting device ID:', error);
    // Fallback to UUID
    return uuidv4();
  }
};

export const btBaseQuery = fetchBaseQuery({
  baseUrl: apiLink,
  prepareHeaders: async (headers: any, {getState}) => {
    try {
      const token = (getState() as RootState).gdata.token;
      const syskey = getUniqueDeviceId();

      headers.set('Accept-Language', (i18n.language || 'en').slice(0, 2));
      if (token) {
        headers.set('auth', `${token}`);
      } else {
        headers.set('syskey', `${syskey}`);
      }
    } catch (error) {
      console.error('Error preparing headers:', error);
      // Set a fallback syskey if there's an error using UUID
      const fallbackKey = uuidv4();
      headers.set('syskey', fallbackKey);
    }
    return headers;
  },
});

export const storeToken = async (jwtToken: string) => {
  try {
    await AsyncStorage.setItem('token', jwtToken);
  } catch (error) {
    // Error saving data
  }
};

export const retrieveToken = async () => {
  try {
    const value = await AsyncStorage.getItem('token');
    if (value !== null) {
      return value;
    }
  } catch (error) {
    return error;
  }
};

export const storeSetting = async (name: string, value: string) => {
  try {
    await AsyncStorage.setItem(name, value);
  } catch (error) {
    console.log(error);
  }
};

export const retrieveSetting = async (name: string): Promise<string> => {
  try {
    const value: string = (await AsyncStorage.getItem(name)) ?? '';
    return value;
  } catch (error) {
    console.error(error); // log error and handle it appropriately
    return ''; // return a default string value
  }
};

export const removeSetting = async (name: string) => {
  try {
    await AsyncStorage.removeItem(name);
    return true;
  } catch (error) {
    return error;
  }
};

export const netState = async () => {
  try {
    const state = await NetInfo.fetch();
    return state.isConnected || false;
  } catch (error) {
    console.error('Error checking net state:', error);
    return false; // Return false or handle error as needed
  }
};

export const modeType = [
  {id: '0', label: 'Credit'},
  {id: '1', label: 'Cash'},
  {id: '2', label: 'Card'},
  {id: '3', label: 'Cheque'},
  {id: '4', label: 'Bank Transfer'},
  {id: '5', label: 'UPI'},
  {id: '6', label: 'Complimentary'},
  {id: '7', label: 'Void'},
];

export const useAsyncState = (initialState: any) => {
  const [state, setState] = useState(initialState);

  const asyncSetState = (value: any) => {
    return new Promise(resolve => {
      setState(value);
      setState((current: any) => {
        resolve(current);
        return current;
      });
    });
  };

  return [state, asyncSetState];
};

export const toUTCDate = (locdt: any) => {
  var localTime = moment(locdt).utc().format('YYYY-MM-DD HH:MM:SS');

  return localTime;
};

var a = [
  '',
  'one ',
  'two ',
  'three ',
  'four ',
  'five ',
  'six ',
  'seven ',
  'eight ',
  'nine ',
  'ten ',
  'eleven ',
  'twelve ',
  'thirteen ',
  'fourteen ',
  'fifteen ',
  'sixteen ',
  'seventeen ',
  'eighteen ',
  'nineteen ',
];
var b = [
  '',
  '',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
];

export const inWords = (num: string) => {
  if ((num = num.toString()).length > 9) {
    return 'overflow';
  }
  let n: any = ('000000000' + num)
    .substr(-9)
    .match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) {
    return;
  }
  var str = '';
  str +=
    n[1] != 0
      ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'crore '
      : '';
  str +=
    n[2] != 0
      ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'lakh '
      : '';
  str +=
    n[3] != 0
      ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'thousand '
      : '';
  str +=
    n[4] != 0
      ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'hundred '
      : '';
  str +=
    n[5] != 0
      ? (str != '' ? 'and ' : '') +
        (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) +
        'only '
      : 'only';
  return str.toUpperCase();
};

export const ShowToast = (strMsg: string, type?: string) => {
  Toast.show({
    type: type ?? 'success',
    text1: strMsg,
  });
};

export const generateUpiLink = (
  upiId: any,
  payeeName: any,
  amount: any,
  transactionNote = '',
) => {
  return `upi://pay?pa=${upiId}&pn=${encodeURIComponent(
    payeeName,
  )}&am=${amount}&cu=INR&tn=${encodeURIComponent(transactionNote)}`;
};

export const getContrastColor = (hexColor: string): string => {
  hexColor = hexColor.trim().replace(/^#/, '');

  const isValidHex = /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hexColor);
  if (!isValidHex) {
    throw new Error('Invalid hex color format');
  }

  if (hexColor.length === 3) {
    hexColor = hexColor
      .split('')
      .map(char => char + char)
      .join('');
  }

  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

